import React, { useState } from "react";
import { filter, identity, isEmpty, reduce, snakeCase, toLower } from "lodash";
import styled from "styled-components";
import { getFacetName } from "@linkfluence/i18n";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { Text } from "@ui-kit/form";
import Modal from "@ui-kit/modal/fullscreen";
import { IProps as IllustrationProps } from "@radarly/components/cards/card-edition/illustration";
import usePasteModalData from "@radarly/components/modals/paste/usePasteModalData";
import Layout from "@radarly/components/panels/card-library/layout";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { useFeature } from "@radarly/core/features/use-cases";
import {
  useCurrentProject,
  useCurrentProjectFocusesId,
} from "@radarly/core/project/use-cases";
import {
  useCurrentUser,
  useCurrentUserLocale,
} from "@radarly/core/user/use-cases";
import { useCurrentWorkspaceId } from "@radarly/core/workspace/use-cases";
import { tr } from "@radarly/i18n";
import {
  Card,
  ClusterCard,
  DateMetricCard,
  DateMetricsCard,
  DateTermCard,
  GeoCard,
  PivotCard,
  PostsCard,
  TermCard,
  UsersCard,
} from "@radarly/models/cards";
import {
  changeTermSelectionMethod,
  termHasSelectionMethods,
} from "@radarly/models/cards/form";
import {
  AICard,
  CardPeriod,
  EmbeddedCard,
  PinnedAuthorCard,
  PinnedPostCard,
  TermMetricsCard,
  TextCard,
} from "@radarly/models/cards/model";
import { ComparisonPeriod } from "@radarly/models/cards/period";
import {
  DateTermCardTerm,
  GeoCardTerm,
  PivotCardTerm,
  TermCardTerm,
  TermMetricsCardTerm,
} from "@radarly/models/cards/term";
import { AICardFactory } from "@radarly/models/cards/types/ai";
import { ClassificationCardFactory } from "@radarly/models/cards/types/classification";
import { ClusterCardFactory } from "@radarly/models/cards/types/cluster";
import { DateMetricCardFactory } from "@radarly/models/cards/types/date.metric";
import { DateMetricsCardFactory } from "@radarly/models/cards/types/date.metrics";
import { DateTermCardFactory } from "@radarly/models/cards/types/date.term";
import { DemographicCardFactory } from "@radarly/models/cards/types/demographic";
import { EmbeddedCardFactory } from "@radarly/models/cards/types/embedded";
import { EmvCardFactory } from "@radarly/models/cards/types/emv";
import { GeoCardFactory } from "@radarly/models/cards/types/geo";
import { PinnedAuthorCardFactory } from "@radarly/models/cards/types/pinned.author";
import { PinnedPostCardFactory } from "@radarly/models/cards/types/pinned.post";
import { PivotCardFactory } from "@radarly/models/cards/types/pivot";
import { PostsCardFactory } from "@radarly/models/cards/types/posts";
import { TermCardFactory } from "@radarly/models/cards/types/term";
import { TermMetricsCardFactory } from "@radarly/models/cards/types/term.metrics";
import { TextCardFactory } from "@radarly/models/cards/types/text";
import { UsersCardFactory } from "@radarly/models/cards/types/users";
import { DatavizName } from "@radarly/models/dataviz";
import { PastedCard } from "@radarly/models/insight-pages/editor";
import { AIFragment } from "@radarly/models/search";
import Cards from "./cards";
import Sidebar from "./sidebar";

interface SharedPanelsCardEditionChartProps {
  addCard: (card: Card) => void;
  onClose: () => void;
  globalPeriod?: CardPeriod;
}

export type CardViz = {
  viz: DatavizName;
  callback: () => void;
  title?: string;
  vizOptions?: IllustrationProps["options"];
  facet?: Facet;
};

export type Section = {
  title?: string;
  sections?: Record<string, Section>;
  cards?: Record<string, CardViz>;
};

export type Sections = Record<string, Section>;

const getTranslation = (locale: Locale, id: string) => {
  return tr(locale, "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::" + id);
};

const StyledText = styled(Text)`
  width: 240px;
`;

export default function SharedPanelsCardEditionCharts({
  addCard,
  onClose,
  globalPeriod,
}: SharedPanelsCardEditionChartProps) {
  const { from, to } = globalPeriod || {};
  const { cards: pastedCards } = usePasteModalData("paste_card", null);
  const [currentSection, setCurrentSection] = useState(null);
  const [searchString, setSearchString] = useState("");

  const locale = useCurrentUserLocale();
  const facets = useFacetsFromContext();
  const workspaceId = useCurrentWorkspaceId();
  const projectFocuses = useCurrentProjectFocusesId();
  const user = useCurrentUser();
  const project = useCurrentProject();
  const isPinnedAuthorEnable = useFeature("pinned_author");
  const imageRecognitionEnabled = useFeature("image_recognition");
  const isEmvCardEnabled = user.projects.find(({ id }) => project.id === id)
    ?.flags.isEmvCardEnabled;
  const isAICardEnabled = useFeature("ai_card");

  const getTranslationWithFacet = (
    title: string,
    facet: Facet,
    label = "",
    transform: (facet: string) => string = identity
  ) => {
    return tr(locale, "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::" + title, {
      facet: transform(label !== "" ? label : getFacetName(locale, facet)),
    });
  };

  const onAddTermCard = (
    title: string,
    field: TermCardTerm,
    dataviz: TermCard["dataviz"]["name"]
  ) => {
    addCard(
      TermCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          term: field,
          from,
          to,
          dataviz,
        }
      )
    );
  };

  const onAddDateMetricCard = (
    title: string,
    dataviz: DateMetricCard["dataviz"]["name"],
    metric,
    comparisonPeriod?: ComparisonPeriod
  ) => {
    addCard(
      DateMetricCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          dataviz,
          from,
          to,
          metric,
          comparisonPeriod,
        }
      )
    );
  };

  const onAddPivotCard = (
    title: string,
    dataviz: PivotCard["dataviz"]["name"],
    term: PivotCardTerm,
    pivot: PivotCardTerm
  ) => {
    addCard(
      PivotCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          dataviz,
          from,
          to,
          term,
          pivot,
          metric: Metric.Doc,
        }
      )
    );
  };

  const onAddDateTermCard = (
    title: string,
    dataviz: DateTermCard["dataviz"]["name"],
    field: DateTermCardTerm
  ) => {
    addCard(
      DateTermCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          dataviz,
          term: field,
          from,
          to,
          metric: Metric.Doc,
        }
      )
    );
  };

  const onAddDateMetricsCard = (
    title: string,
    dataviz: DateMetricsCard["dataviz"]["name"],
    metric
  ) => {
    addCard(
      DateMetricsCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          from,
          to,
          metrics: [Metric.Doc, metric],
        }
      )
    );
  };

  const onAddDemographicCard = (title: string) => {
    addCard(
      DemographicCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          from,
          to,
          metric: Metric.Doc,
        }
      )
    );
  };

  const onAddEmvCard = (title: string, comparisonPeriod?: ComparisonPeriod) => {
    addCard(
      EmvCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        { from, to, comparisonPeriod }
      )
    );
  };

  const onAddGeoCard = (
    title: string,
    dataviz: GeoCard["dataviz"]["name"],
    field: GeoCardTerm
  ) => {
    addCard(
      GeoCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          from,
          to,
          dataviz,
          metric: Metric.Doc,
          term: field,
        }
      )
    );
  };

  const onAddPostsCard = (
    title: string,
    dataviz: PostsCard["dataviz"]["name"]
  ) => {
    addCard(
      PostsCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          from,
          to,
          dataviz,
          sortBy: Metric.Engagement,
        }
      )
    );
  };

  const onAddClusterCard = (
    title: string,
    dataviz: ClusterCard["dataviz"]["name"]
  ) => {
    addCard(
      ClusterCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          from,
          to,
          dataviz,
        }
      )
    );
  };

  const onAddUsersCard = (
    title: string,
    dataviz: UsersCard["dataviz"]["name"]
  ) => {
    addCard(
      UsersCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          from,
          to,
          dataviz,
          sortBy: Metric.Engagement,
        }
      )
    );
  };

  const onAddTermMetricsCard = (
    title: string,
    dataviz: TermMetricsCard["dataviz"]["name"],
    field: TermMetricsCardTerm
  ) => {
    addCard(
      TermMetricsCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          from,
          to,
          dataviz,
          term: field,
        }
      )
    );
  };

  const onAddEmbeddedCard = (
    title: string,
    dataviz: EmbeddedCard["dataviz"]["name"]
  ) => {
    addCard(
      EmbeddedCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          dataviz,
        }
      )
    );
  };

  const onAddTextCard = (
    title: string,
    dataviz: TextCard["dataviz"]["name"]
  ) => {
    addCard(
      TextCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          dataviz,
        }
      )
    );
  };

  const onAddIACard = (
    title: string,
    dataviz: AICard["dataviz"]["name"],
    type?: AIFragment["analysis"]["type"],
    includeNegativeAnalysis?: AIFragment["analysis"]["includeNegativeAnalysis"],
    useInsightPageAIContext?: AIFragment["analysis"]["useInsightPageAIContext"]
  ) => {
    addCard(
      AICardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          dataviz,
          type,
          useInsightPageAIContext,
          includeNegativeAnalysis,
        }
      )
    );
  };

  const onAddPinnedPostCard = (
    title: string,
    dataviz: PinnedPostCard["dataviz"]["name"]
  ) => {
    addCard(
      PinnedPostCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          dataviz,
        }
      )
    );
  };

  const onAddPinnedAuthorCard = (
    title: string,
    dataviz: PinnedAuthorCard["dataviz"]["name"]
  ) => {
    addCard(
      PinnedAuthorCardFactory.getInstance().fromInput(
        workspaceId,
        projectFocuses,
        title,
        {
          dataviz,
        }
      )
    );
  };

  const onAddPastedCard = (pastedCard: PastedCard) => {
    const { vizOptions, color, ...card } = pastedCard;
    addCard(card);
  };

  const getHorizontalBarchartCard = (facet: TermCardTerm, title: string) => ({
    viz: DatavizName.HorizontalBarChart,
    callback: () => onAddTermCard(title, facet, DatavizName.HorizontalBarChart),
    title,
    facet,
  });

  const getRankingCard = (facet: TermCardTerm, label: string) =>
    getHorizontalBarchartCard(
      facet,
      getTranslationWithFacet("RANKING_CARD", facet, label)
    );

  const getShareOfVoiceCard = (facet: TermCardTerm, title: string) => ({
    viz: DatavizName.DoughnutChart,
    callback: () => onAddTermCard(title, facet, DatavizName.DoughnutChart),
    facet,
    title,
  });

  const getPostsOverTimeCard = (
    facet: DateTermCardTerm,
    title: string,
    postOverTimeChart
  ) => ({
    viz: postOverTimeChart,
    callback: () => onAddDateTermCard(title, postOverTimeChart, facet),
    facet,
    title,
  });

  const getGlobalPerfCard = (facet: TermMetricsCardTerm, title: string) => ({
    viz: DatavizName.BubbleChart,
    callback: () => onAddTermMetricsCard(title, DatavizName.BubbleChart, facet),
    facet,
    title,
  });

  const getEmbeddedCard = (title: string) => ({
    viz: DatavizName.Embedded,
    callback: () => onAddEmbeddedCard(title, DatavizName.Embedded),
    title,
  });

  const getTextCard = (title: string) => ({
    viz: DatavizName.Text,
    callback: () => onAddTextCard(title, DatavizName.Text),
    title,
  });

  const getAICard = (
    title: string,
    type?: AIFragment["analysis"]["type"],
    includeNegativeAnalysis?: AIFragment["analysis"]["includeNegativeAnalysis"],
    useInsightPageAIContext?: AIFragment["analysis"]["useInsightPageAIContext"]
  ) => ({
    viz: DatavizName.AI,
    callback: () =>
      onAddIACard(
        title,
        DatavizName.AI,
        type,
        useInsightPageAIContext,
        includeNegativeAnalysis
      ),
    title,
  });

  const getPinnedPostCard = (title: string) => ({
    viz: DatavizName.PinnedPost,
    callback: () => onAddPinnedPostCard(title, DatavizName.PinnedPost),
    title,
  });

  const getPinnedAuthorCard = (title: string) => ({
    viz: DatavizName.PinnedAuthor,
    callback: () => onAddPinnedAuthorCard(title, DatavizName.PinnedAuthor),
    title,
  });

  const getSplitBySentimentCard = (facet: PivotCardTerm, title: string) => {
    const against = facet === Facet.Tones ? Facet.Platforms : Facet.Tones;
    return {
      viz: DatavizName.HorizontalStackedBarChart,
      callback: () =>
        onAddPivotCard(
          title,
          DatavizName.HorizontalStackedBarChart,
          facet,
          against
        ),
      facet: against,
      title,
    };
  };

  const getCloudCard = (facet: TermCardTerm, title: string = ""): CardViz => {
    const cardTitle = title !== "" ? title : getFacetName(locale, facet);
    const card = {
      viz: DatavizName.Cloud,
      callback: () => onAddTermCard(cardTitle, facet, DatavizName.Cloud),
      title: cardTitle,
      vizOptions: {},
      facet: facet,
    };

    if (title !== "") {
      card.title = title;
    }

    if (facet === Facet.EmojiCharts) {
      card.vizOptions = { emojis: true };
    }

    return card;
  };

  const getContentClassificationCard = (): CardViz => {
    return {
      viz: DatavizName.Sunburst,
      callback: () =>
        addCard(
          ClassificationCardFactory.getInstance().fromInput(
            workspaceId,
            projectFocuses,
            getTranslation(locale, "content_classification_sunburst"),
            {
              from,
              to,
              dataviz: DatavizName.Sunburst,
            }
          )
        ),
    };
  };

  const getImageRecognitionCard = (facet: TermCardTerm) => {
    const title = getFacetName(locale, facet);
    return {
      viz: DatavizName.HorizontalBarChart,
      callback: () =>
        onAddTermCard(title, facet, DatavizName.HorizontalBarChart),
      title,
      facet,
    };
  };

  const getReducedCardList = (facet, label: string = "") => {
    const cards: Record<string, CardViz> = {
      [facet + "_ranking"]: getRankingCard(facet, label),
    };
    if (termHasSelectionMethods(facet)) {
      const significantFacet = changeTermSelectionMethod(
        facet,
        "significant"
      ) as TermCardTerm;
      const emergingFacet = changeTermSelectionMethod(
        facet,
        "emerging"
      ) as TermCardTerm;

      cards[facet + "_cloud"] = getCloudCard(
        facet,
        getTranslationWithFacet("TERM_FREQUENCY", facet, label)
      );
      cards[significantFacet + "_cloud"] = getCloudCard(
        significantFacet,
        getTranslationWithFacet("TERM_SIGNIFICANT", facet, label)
      );
      cards[emergingFacet + "_cloud"] = getCloudCard(
        emergingFacet,
        getTranslationWithFacet("TERM_EMERGING", facet, label)
      );
    } else {
      cards[facet + "_cloud"] = getCloudCard(
        facet,
        getTranslationWithFacet("FACET_CLOUD", facet, label)
      );
    }

    return {
      cards,
      title: label !== "" ? label : getFacetName(locale, facet),
    };
  };

  const getDefaultCardList = (facet, label = "") => {
    const postOverTimeChart =
      facet === Facet.Tones
        ? DatavizName.StackedBarChart
        : DatavizName.MultipleLineChart;
    return {
      cards: {
        [facet + "_ranking"]: getRankingCard(facet, label),
        [facet + "_share_of_voice"]: getShareOfVoiceCard(
          facet,
          getTranslationWithFacet("SHARE_OF_VOICE_CARD", facet, label)
        ),
        [facet + "_posts_over_time"]: getPostsOverTimeCard(
          facet,
          getTranslationWithFacet("POSTS_OVER_TIME_CARD", facet, label),
          postOverTimeChart
        ),
        [facet + "_global_performance"]: getGlobalPerfCard(
          facet,
          getTranslationWithFacet("GLOBAL_PERF_CARD", facet, label)
        ),
        [facet +
        "_split_by_" +
        (facet === Facet.Tones ? "platforms" : "sentiment")]:
          getSplitBySentimentCard(
            facet,
            getTranslationWithFacet(
              facet === Facet.Tones
                ? "SPLIT_BY_PLATFORMS"
                : "SPLIT_BY_SENTIMENT",
              facet,
              label
            )
          ),
      },
    };
  };

  const getGeoBarchartCard = (facet: GeoCardTerm, title: string) => ({
    viz: DatavizName.HorizontalBarChart,
    callback: () => onAddGeoCard(title, DatavizName.HorizontalBarChart, facet),
    title,
  });

  const getGeoMapCard = (facet: GeoCardTerm, title: string) => ({
    viz: DatavizName.Map,
    callback: () => onAddGeoCard(title, DatavizName.Map, facet),
    title,
    vizOptions: facet !== Facet.Countries ? { [facet]: true } : {},
  });

  const getGeoCardList = (facet: GeoCardTerm) => ({
    cards: {
      [facet + "_comparison"]: getGeoBarchartCard(
        facet,
        getTranslationWithFacet("RANKING_CARD", facet)
      ),
      [facet + "_map"]: getGeoMapCard(
        facet,
        getTranslationWithFacet("FACET_MAP", facet)
      ),
    },
    title: getFacetName(locale, facet),
  });

  const AI_CARDS_TYPE: Array<AIFragment["analysis"]["type"]> = [
    "general-analysis",
    "signal-analysis",
    "risk-analysis",
    "audience-analysis",
  ];

  const getAICardList = () =>
    reduce(
      AI_CARDS_TYPE,
      (acc, aiCardType) => ({
        ...acc,
        [aiCardType]: getAICard(
          getTranslation(locale, snakeCase(aiCardType)),
          aiCardType,
          true,
          false
        ),
      }),
      {}
    );

  const getImageRecognitionCardList = () => {
    const defaultCards = {
      imageRecognitionPersons: getImageRecognitionCard(
        Facet.ImageRecognitionPersons
      ),
      imageRecognitionEmotions: getImageRecognitionCard(
        Facet.ImageRecognitionEmotions
      ),
      imageRecognitionGenders: getImageRecognitionCard(
        Facet.ImageRecognitionGenders
      ),
      imageRecognitionAgeGroups: getImageRecognitionCard(
        Facet.ImageRecognitionAge
      ),
      imageRecognitionObjects: getImageRecognitionCard(
        Facet.ImageRecognitionObjects
      ),
      imageRecognitionScenes: getImageRecognitionCard(
        Facet.ImageRecognitionScenes
      ),
    };
    const hiddenEmptyFacetCards = imageRecognitionEnabled
      ? {
          ...(!isEmpty(facets["imageRecognitionLogos"].values) && {
            imageRecognitionLogos: getImageRecognitionCard(
              Facet.ImageRecognitionLogos
            ),
          }),
          ...(!isEmpty(
            facets["imageRecognitionPersonIdentifications"].values
          ) && {
            imageRecognitionPersonIdentifications: getImageRecognitionCard(
              Facet.ImageRecognitionPersonIdentifications
            ),
          }),
        }
      : {};

    return {
      cards: {
        ...defaultCards,
        ...hiddenEmptyFacetCards,
      },
    };
  };

  const getCustomFieldsCards = () => {
    return reduce(
      filter(facets, { custom: true }),
      (acc, field) => {
        acc[field.id] =
          field.type === "closed"
            ? {
                ...getDefaultCardList(field.id, field.label),
                title: field.label,
              }
            : {
                ...getReducedCardList(field.id, field.label),
                title: field.label,
              };
        return acc;
      },
      {}
    );
  };

  const getKpiCard = (title: string, metric: Metric) => ({
    viz: DatavizName.Kpi,
    callback: () =>
      onAddDateMetricCard(title, DatavizName.Kpi, metric, "previous_period"),
    vizOptions: { showLine: true },
  });

  const DATAVIZ_SECTIONS: Sections = {
    posts: {
      cards: {
        top_posts: {
          viz: DatavizName.TopPosts,
          callback: () =>
            onAddPostsCard(
              getTranslation(locale, "top_posts"),
              DatavizName.TopPosts
            ),
        },
        picture_wall: {
          viz: DatavizName.PictureWall,
          callback: () =>
            onAddPostsCard(
              getTranslation(locale, "picture_wall"),
              DatavizName.PictureWall
            ),
        },
        image_clustering: {
          viz: DatavizName.ImageClustering,
          callback: () =>
            onAddClusterCard(
              getTranslation(locale, "image_clustering"),
              DatavizName.ImageClustering
            ),
        },
        top_authors: {
          viz: DatavizName.TopPosts,
          callback: () =>
            onAddUsersCard(
              getTranslation(locale, "top_authors"),
              DatavizName.TopAuthors
            ),
        },
      },
    },
    basics: {
      sections: {
        kpi: {
          cards: {
            post_volume: getKpiCard(
              getTranslation(locale, "post_volume"),
              Metric.Doc
            ),
            audience: getKpiCard(
              getTranslation(locale, "audience"),
              Metric.Reach
            ),
            engagement: getKpiCard(
              getTranslation(locale, "engagement"),
              Metric.Engagement
            ),
            net_sentiment: getKpiCard(
              getTranslation(locale, "net_sentiment"),
              Metric.NetSentiment
            ),
            average_engagement_per_post: getKpiCard(
              getTranslation(locale, "average_engagement_per_post"),
              Metric.EngagementPerPost
            ),
          },
        },
        kpi_over_time: {
          cards: {
            post_over_time: {
              viz: DatavizName.LineChart,
              callback: () =>
                onAddDateMetricCard(
                  getTranslation(locale, "post_over_time"),
                  DatavizName.LineChart,
                  Metric.Doc,
                  null
                ),
            },
            post_and_audience_over_time: {
              viz: DatavizName.DoubleTimeChart,
              callback: () =>
                onAddDateMetricsCard(
                  getTranslation(locale, "post_and_audience_over_time"),
                  DatavizName.DoubleTimeChart,
                  Metric.Reach
                ),
            },
            post_and_engagement_over_time: {
              viz: DatavizName.DoubleTimeChart,
              callback: () =>
                onAddDateMetricsCard(
                  getTranslation(locale, "post_and_engagement_over_time"),
                  DatavizName.DoubleTimeChart,
                  Metric.Engagement
                ),
            },
          },
        },
        queries: getDefaultCardList(Facet.Focuses),
        platforms: getDefaultCardList(Facet.Platforms),
        sentiment: getDefaultCardList(Facet.Tones),
        languages: getReducedCardList(Facet.Languages),
        hashtags: getReducedCardList(Facet.Hashtags),
      },
    },
    text_analysis: {
      sections: {
        ...(isAICardEnabled && {
          IA_ANALYSIS: {
            cards: {
              ...getAICardList(),
            },
          },
        }),
        entities: {
          cards: {
            entities: getCloudCard(
              Facet.Entities,
              getTranslationWithFacet(
                "TERM_FREQUENCY",
                Facet.Entities,
                "",
                toLower
              )
            ),
            significantNamedEntities: getCloudCard(
              Facet.SignificantNamedEntities,
              getTranslationWithFacet(
                "TERM_SIGNIFICANT",
                Facet.Entities,
                "",
                toLower
              )
            ),
            emergingNamedEntities: getCloudCard(
              Facet.EmergingNamedEntities,
              getTranslationWithFacet(
                "TERM_EMERGING",
                Facet.Entities,
                "",
                toLower
              )
            ),
          },
        },
        keyphrases: {
          cards: {
            keyphrases: getCloudCard(
              Facet.Keyphrases,
              getTranslationWithFacet(
                "TERM_FREQUENCY",
                Facet.Keyphrases,
                "",
                toLower
              )
            ),
            significantKeyphrases: getCloudCard(
              Facet.SignificantKeyphrases,
              getTranslationWithFacet(
                "TERM_SIGNIFICANT",
                Facet.Keyphrases,
                "",
                toLower
              )
            ),
            emergingKeyphrases: getCloudCard(
              Facet.EmergingKeyphrases,
              getTranslationWithFacet(
                "TERM_EMERGING",
                Facet.Keyphrases,
                "",
                toLower
              )
            ),
          },
        },
        mentions: {
          cards: {
            mentions: getCloudCard(
              Facet.Mentions,
              getTranslationWithFacet(
                "TERM_FREQUENCY",
                Facet.Mentions,
                "",
                toLower
              )
            ),
            significantMentions: getCloudCard(
              Facet.SignificantMentions,
              getTranslationWithFacet(
                "TERM_SIGNIFICANT",
                Facet.Mentions,
                "",
                toLower
              )
            ),
            emergingMentions: getCloudCard(
              Facet.EmergingMentions,
              getTranslationWithFacet(
                "TERM_EMERGING",
                Facet.Mentions,
                "",
                toLower
              )
            ),
          },
        },
        content: {
          cards: {
            content_classification: getContentClassificationCard(),
            topics: getCloudCard(Facet.Topics),
          },
        },
        mood: {
          cards: {
            affects: getCloudCard(Facet.Affects),
            emotions: getCloudCard(Facet.Emotions),
          },
        },
        emojis: {
          cards: {
            emojis: getCloudCard(Facet.EmojiCharts),
          },
        },
      },
    },
    image_recognition: getImageRecognitionCardList(),
    posts_origin: {
      sections: {
        countries: getGeoCardList(Facet.Countries),
        regions: getGeoCardList(Facet.Regions),
        towns: getGeoCardList(Facet.Towns),
      },
    },
    authors: {
      cards: {
        authors_gender: {
          viz: DatavizName.DoughnutChart,
          callback: () =>
            onAddTermCard(
              getTranslation(locale, "authors_gender"),
              Facet.Genders,
              DatavizName.DoughnutChart
            ),
          facet: Facet.Genders,
        },
        authors_demographic: {
          viz: DatavizName.Demographic,
          callback: () =>
            onAddDemographicCard(getTranslation(locale, "authors_demographic")),
        },
        authors_occupations: getCloudCard(
          Facet.Occupations,
          getTranslation(locale, "authors_occupations")
        ),
      },
    },
    misc: {
      cards: {
        reviews: getHorizontalBarchartCard(
          Facet.ReviewRate,
          getFacetName(locale, Facet.ReviewRate)
        ),
        devices: getHorizontalBarchartCard(
          Facet.Devices,
          getFacetName(locale, Facet.Devices)
        ),
      },
    },
    editorial: {
      cards: {
        embedded: getEmbeddedCard(getTranslation(locale, "embedded")),
      },
    },
    custom_fields: {
      sections: getCustomFieldsCards(),
    },
  };

  DATAVIZ_SECTIONS.editorial.cards.text = getTextCard(
    getTranslation(locale, "text")
  );

  DATAVIZ_SECTIONS.editorial.cards.pinnedPost = getPinnedPostCard(
    getTranslation(locale, "pinned-post")
  );
  if (isPinnedAuthorEnable) {
    DATAVIZ_SECTIONS.editorial.cards.pinnedAuthor = getPinnedAuthorCard(
      getTranslation(locale, "pinned-author")
    );
  }

  if (isEmvCardEnabled) {
    DATAVIZ_SECTIONS.misc.cards.emv = {
      viz: DatavizName.Emv,
      callback: () =>
        onAddEmvCard(getTranslation(locale, "emv"), "previous_period"),
    };
  }

  return (
    <Modal
      title={tr(locale, "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::CHOOSE_CARD")}
      onClose={onClose}
      options={
        <StyledText
          variant="primary"
          placeholder={tr(
            locale,
            "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::SEARCH_PLACEHOLDER"
          )}
          onChange={setSearchString}
          value={searchString}
          isClearable
        />
      }
    >
      <Layout
        displaySidebar={searchString === ""}
        sidebar={
          <Sidebar
            sections={DATAVIZ_SECTIONS}
            setCurrentSection={setCurrentSection}
          />
        }
        body={
          <Cards
            sections={DATAVIZ_SECTIONS}
            currentSection={currentSection}
            search={searchString}
            pastedCards={pastedCards}
            onAddPastedCard={onAddPastedCard}
          />
        }
      />
    </Modal>
  );
}
